import { chakra } from '@chakra-ui/react';
import { showLinkModal } from '@src/components/sco/RichMarkdownInput/utilities/blocks';
import { Element, Node, Transforms } from 'slate';
import { RenderElementProps, useSelected, useSlate } from 'slate-react';

// Define a custom type for link elements
interface LinkElementType extends Element {
  url: string;
  type: string;
}
type LinkElementProps = RenderElementProps & {
  element: LinkElementType;
};
export function LinkElement(props: LinkElementProps) {
  // Skip rendering mention links as clickable anchors
  if (props.element.url?.startsWith('mention:')) {
    return <span {...props.attributes}>{props.children}</span>;
  }
  const editor = useSlate();
  const selected = useSelected();
  const handleClick = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) {
      // Allow normal link behavior when Ctrl/Cmd is pressed
      return;
    }
    e.preventDefault();
    if (selected) {
      // If already selected, show modal to edit the URL and label
      const linkText = Node.string(props.element);
      const path = props.element.url ? editor.selection.anchor.path.slice(0, -1) : editor.selection.anchor.path;
      showLinkModal(props.element.url || '', linkText || '', 'Edit Link', (url, label) => {
        // Only update if URL is not empty to prevent link removal
        if (url) {
          // Update the URL
          Transforms.setNodes<LinkElementType>(editor, {
            url
          }, {
            at: path
          });

          // If label is different from the current text and different from the URL,
          // update the text content
          if (label && label !== linkText && label !== url) {
            // Remove all children
            for (let i = props.element.children.length - 1; i >= 0; i--) {
              Transforms.removeNodes(editor, {
                at: [...path, i]
              });
            }

            // Insert new text node with the label
            Transforms.insertNodes(editor, {
              text: label
            }, {
              at: [...path, 0]
            });
          }
        }
      },
      // Remove link functionality
      () => {
        Transforms.unwrapNodes(editor, {
          at: path
        });
      });
    }
  };
  return <chakra.a {...props.attributes} _hover={{
    color: 'blue.600'
  }} color={'blue.500'} css={selected ? {
    boxShadow: '0 0 0 2px #B4D5FF'
  } : {}} href={props.element?.url || '#'} target={'_blank'} textDecoration={'underline'} onClick={handleClick}>
      {props.children}
    </chakra.a>;
}